import { auth } from '../../cross-app-frontend/firebase';
import type {
  CasOrCategory,
  ChemicalExemptionID,
  FacilityGroupID,
  FacilityID,
  FileID,
  ProductChemicalCompositionID,
  ProductExemptionTypeID,
  ProductID,
  ProductUsageID,
  ReleaseProfileID,
  ReleaseProfileQuestionID,
  ReleaseProfileVariableID,
  FormRSubmissionStatus,
  TenantID,
  UsageTypeID,
  UsageUnits,
  ReleaseProfileAssignmentID,
  ReleaseProfileTemplateID,
  Section8GapFillingModeRecord,
  ReleaseProfileAttestationState,
  EmissionsUnitTypeID,
  EmissionsUnitID,
  EmissionsLimitID,
  EmissionsUnit,
  EmissionsVariableAnswerFrequency,
  EmissionsPeriod,
  EmissionsReportID,
  UserID,
  DataSourceID,
  DataSourceImportID,
  ProductUsageExcelFileConfigID,
  ProductUsageSheetConfig,
  ChemicalProfileDataSourceID,
  ChemicalForcedMode,
} from '../../tri-shared/types/db';
import type {
  ReleaseProfileChemicalProfile,
  ReleaseProfileConstant,
  ReleaseProfileFormula,
  ReleaseProfileFormulaChoiceID,
  ReleaseProfileFormulaChoiceListID,
  ReleaseProfileMixCalculation,
  ReleaseProfileQuestion,
  ReleaseProfileUsageMix,
  ReleaseProfileVariableForFrontendCreate,
  ReleaseProfileVariableForFrontendUpdate,
  ReleaseProfileWasteStream,
  ReleaseProfileTemplateVariable,
} from '../../tri-shared/types/release-profiles';
import type {
  ChemicalBrief,
  FormRInputsSnapshot,
  ProductUsageComparisonItem,
  QuestionnaireComparisonItem,
  ReleaseProfileAssignmentNew,
  ThresholdBrief,
  Warning,
} from '../../tri-shared/types/tri';
import type { HazardClassification } from '../../tri-shared/services/hazard-classification';
import type {
  EmissionsLimitPollutantSelector,
  EmissionsLimitType,
  EmissionsLimitChemicalGrouping,
  EmissionsLimitUnitGrouping,
  EmissionsLimitWindow,
  EmissionsLimitWindowReduction,
  EmissionsPollutantLimit,
  EmissionsComplianceMetricConfig,
  EmissionsBasis,
  PotentialEmissionsOutputSelection,
} from '../../tri-shared/types/emissions';
import type { PotentialEmissionsBasisPeriod } from '../../tri-shared/types/release-profiles';
import type {
  OffSiteLocationID,
  PotwID,
  QuestionnaireChemicalActivitiesAndUsesContainer,
  QuestionnaireOffSiteLocation,
  QuestionnaireOnSiteEnergyRecovery,
  QuestionnaireOnSiteRecycling,
  QuestionnairePotw,
  QuestionnairePotwDistributionsKnownChemical,
  QuestionnaireProductionOrActivityRatio,
  QuestionnaireSourceReductionActivitiesContainer,
  QuestionnaireStream,
  QuestionnaireWasteTreatmentContainer,
  Section4MaxOnSiteAmount,
  StreamID,
  TransferID,
  WasteTransfer,
} from '../../tri-shared/types/questionnaires';
import type { TRIQuantityBasisEstimationCodeType } from '../../tri-shared/types/form-r';
import type { Email, ISO8601DateOnly, ISO8601Timestamp } from '../../cross-app-shared/types/common';
import type { ChemicalEditing } from '../../tri-shared/types/chemical-types';
import type { FormRLeafLine } from '../../tri-shared/services/form-r/form-r-sections';
import { facilityStatusUpdateTrigger } from '@/components/progress/ProgressTracker.vue';
import { backendRequest, complianceBackendRequest, hazWasteBackendRequest } from '@/services/backend-request';
import { crossAppActions } from '../../cross-app-frontend/cross-app-actions';
import type { PipelineOperatorAnnotated } from '../../tri-shared/services/reports/report-pipelines';
import { backendAnonymousRequest } from '@/services/backend-request.ts';
import type { HazWasteBackendTypes } from '../../hazwaste-backend/hazwaste-backend';
import type { ComplianceBackendTypes } from '../../compliance-backend/compliance-backend';
import type { TriBackendTypes } from '../../tri-backend/tri-backend';

async function askQuestionFromVectorStore(
  question: string,
  onStreamUpdate?: (text: string, done: boolean, sources?: any[], annotations?: any[]) => void,
): Promise<{
  choices?: Array<{ message?: { content?: string } }>;
  response?: string;
  message?: string;
  sources?: Array<{
    filename: string;
    file_id: string;
    text?: string;
    score?: number;
  }>;
}> {
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new Error('User must be logged in to use AI assistant');
  }

  const response = await fetch(`${FIREBASE_FUNCTIONS_BASE_URL}/askQuestionFromVectorStore`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ question }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  // Check if this is a streaming response
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('text/event-stream')) {
    // Handle SSE stream
    if (onStreamUpdate) {
      // If we have a streaming handler, use it for real-time updates
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedText = '';
      let sources: any[] = [];

      console.log('Starting stream processing');

      // Send initial empty response to show UI is ready
      onStreamUpdate('', false);

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('Stream completed, final text length:', accumulatedText.length);
            // Final update with complete text
            onStreamUpdate(
              accumulatedText || 'No response received from AI assistant',
              true,
              sources.length > 0 ? sources : undefined,
            );
            break;
          }

          // Add chunk to buffer
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete SSE messages
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          let _currentEventType = '';

          for (const line of lines) {
            // Track event type
            if (line.startsWith('event: ')) {
              _currentEventType = line.substring(7).trim();
              continue;
            }

            // Process data lines
            if (line.startsWith('data: ')) {
              const data = line.substring(6).trim();
              if (data === '[DONE]') continue;

              try {
                const parsedData = JSON.parse(data);

                // Handle delta text content - this is the key fix
                if (parsedData.type === 'response.output_text.delta' && parsedData.delta) {
                  // Extract the delta text and immediately update UI
                  const deltaText = parsedData.delta;
                  accumulatedText += deltaText;
                  onStreamUpdate(accumulatedText, false, sources);
                  console.log('Delta processed:', deltaText, 'Total length:', accumulatedText.length);
                }
                // Handle file search results
                else if (parsedData.type === 'file_search_call' && parsedData.results) {
                  sources = parsedData.results.map((result: any) => ({
                    filename: result.filename,
                    file_id: result.file_id,
                    text: result.text || result.content || result.snippet || 'Source text not available',
                    score: result.score || 0,
                  }));
                  onStreamUpdate(accumulatedText, false, sources);
                  console.log(
                    'Sources updated:',
                    sources.length,
                    'First source text length:',
                    sources[0]?.text?.length || 0,
                  );
                }
                // Handle annotations/citations
                else if (
                  parsedData.type === 'response.output_text.annotation.added' &&
                  parsedData.annotation &&
                  parsedData.annotation.type === 'file_citation'
                ) {
                  const citation = {
                    filename: parsedData.annotation.filename,
                    file_id: parsedData.annotation.file_id,
                    text:
                      parsedData.annotation.text ||
                      parsedData.annotation.content ||
                      parsedData.annotation.snippet ||
                      'Source text not available',
                    score: parsedData.annotation.score || 0,
                  };

                  if (!sources.some((s) => s.file_id === citation.file_id)) {
                    sources.push(citation);
                    onStreamUpdate(accumulatedText, false, sources);
                    console.log('Citation added:', citation.filename, 'Text length:', citation.text?.length || 0);
                  }
                }
                // Handle completion
                else if (parsedData.type === 'response.completed') {
                  console.log('🏁 Response completed event received');
                  console.log('Full parsedData:', JSON.stringify(parsedData, null, 2));

                  // Extract sources from the complete response structure
                  if (parsedData.response && parsedData.response.output) {
                    console.log('📋 Found response.output with', parsedData.response.output.length, 'items');

                    // Log all output items
                    parsedData.response.output.forEach((item: any, index: number) => {
                      console.log(`Output item ${index}:`, {
                        type: item.type,
                        id: item.id,
                        hasSearchResults: !!item.search_results,
                        searchResultsLength: item.search_results?.length || 0,
                      });

                      if (item.type === 'file_search_call' && item.search_results) {
                        console.log(`File search results for item ${index}:`, item.search_results);
                      }
                    });

                    // Find the message output with annotations
                    const messageOutput = parsedData.response.output.find(
                      (item: any) => item.type === 'message' && item.content && item.content.length > 0,
                    );

                    console.log('📨 Message output found:', !!messageOutput);
                    if (messageOutput) {
                      console.log('Message content items:', messageOutput.content.length);

                      const textContent = messageOutput.content.find(
                        (content: any) => content.type === 'output_text' && content.annotations,
                      );

                      console.log('📝 Text content with annotations found:', !!textContent);
                      if (textContent && textContent.annotations) {
                        console.log('📎 Found', textContent.annotations.length, 'annotations');

                        // Log all annotations
                        textContent.annotations.forEach((ann: any, index: number) => {
                          console.log(`Annotation ${index}:`, {
                            type: ann.type,
                            filename: ann.filename,
                            file_id: ann.file_id,
                            index: ann.index,
                          });
                        });

                        // Extract file citations with proper text lookup
                        const citations = textContent.annotations
                          .filter((ann: any) => ann.type === 'file_citation')
                          .map((citation: any) => {
                            console.log('🔍 Processing citation:', citation.filename, 'file_id:', citation.file_id);

                            // Try to find the corresponding file search results for this citation
                            const fileSearchOutput = parsedData.response.output.find(
                              (item: any) => item.type === 'file_search_call' && item.results,
                            );

                            console.log('🔎 File search output found:', !!fileSearchOutput);
                            if (fileSearchOutput) {
                              console.log('Search results count:', fileSearchOutput.results?.length || 0);
                            }

                            let sourceText = 'Source text not available';
                            if (fileSearchOutput && fileSearchOutput.results) {
                              console.log('🔍 Searching for matching result with file_id:', citation.file_id);

                              // Log all search results
                              fileSearchOutput.results.forEach((result: any, index: number) => {
                                console.log(`Search result ${index}:`, {
                                  filename: result.filename,
                                  file_id: result.file_id,
                                  hasText: !!result.text,
                                  textLength: result.text?.length || 0,
                                  textPreview: result.text?.substring(0, 100) + '...',
                                });
                              });

                              const matchingResult = fileSearchOutput.results.find(
                                (result: any) => result.file_id === citation.file_id,
                              );

                              console.log('🎯 Matching result found:', !!matchingResult);
                              if (matchingResult) {
                                console.log('Matching result details:', {
                                  filename: matchingResult.filename,
                                  hasText: !!matchingResult.text,
                                  textLength: matchingResult.text?.length || 0,
                                });

                                if (matchingResult.text) {
                                  sourceText = matchingResult.text;
                                  console.log('✅ Source text extracted, length:', sourceText.length);
                                } else {
                                  console.log('❌ Matching result has no text property');
                                  console.log('Available properties:', Object.keys(matchingResult));
                                }
                              } else {
                                console.log('❌ No matching result found for file_id:', citation.file_id);
                              }
                            } else {
                              console.log('❌ No file search output or results found');
                            }

                            const finalCitation = {
                              filename: citation.filename,
                              file_id: citation.file_id,
                              text: sourceText,
                              score: citation.score || 0,
                            };

                            console.log('📋 Final citation created:', {
                              filename: finalCitation.filename,
                              textLength: finalCitation.text.length,
                              isDefault: finalCitation.text === 'Source text not available',
                            });

                            return finalCitation;
                          });

                        if (citations.length > 0) {
                          sources = citations;
                          console.log('✅ Sources extracted from completion:', sources.length);
                          sources.forEach((source, index) => {
                            console.log(`Source ${index}:`, {
                              filename: source.filename,
                              textLength: source.text?.length || 0,
                              isDefault: source.text === 'Source text not available',
                            });
                          });

                          // Pass annotations as a separate parameter
                          onStreamUpdate(accumulatedText, true, sources, textContent.annotations);
                        } else {
                          console.log('❌ No citations extracted');
                          onStreamUpdate(accumulatedText, true, sources);
                        }
                      } else {
                        console.log('❌ No text content with annotations found');
                        onStreamUpdate(accumulatedText, true, sources);
                      }
                    } else {
                      console.log('❌ No message output found');
                      onStreamUpdate(accumulatedText, true, sources);
                    }
                  } else {
                    console.log('❌ No response.output found');
                    onStreamUpdate(accumulatedText, true, sources);
                  }
                }
                // Handle OpenAI-style streaming (fallback)
                else if (parsedData.choices && parsedData.choices[0]?.delta?.content) {
                  const content = parsedData.choices[0].delta.content;
                  accumulatedText += content;
                  onStreamUpdate(accumulatedText, false, sources);
                }
              } catch (e) {
                console.warn('Failed to parse SSE data:', data, e);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error reading stream:', error);
        onStreamUpdate(accumulatedText + '\n\n[Error reading response stream]', true, sources);
      }

      return {
        response: accumulatedText || 'No response received',
        sources: sources.length > 0 ? sources : undefined,
      };
    } else {
      // Fallback to original non-streaming implementation
      const responseText = await response.text();

      // Parse SSE response
      const result: {
        response: string;
        sources?: Array<{
          filename: string;
          file_id: string;
          text?: string;
          score?: number;
        }>;
      } = { response: '' };

      const lines = responseText.split('\n');
      let fileSearchResults: any[] = [];
      let extractedContent = '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.substring(6).trim();
          if (data === '[DONE]') continue;

          try {
            const parsedData = JSON.parse(data);

            if (parsedData.type === 'event') {
              // Just log events
              console.log('Event received:', parsedData.name);
            }
            // Handle file search results - usually comes from file_search_call event
            else if (parsedData.type === 'file_search_call' && parsedData.results) {
              fileSearchResults = parsedData.results;
            }
            // Extract content from response.content_part.done event
            else if (parsedData.type === 'response.content_part.done' && parsedData.part) {
              const part = parsedData.part;
              if (part.type === 'output_text') {
                // Extract text content
                extractedContent = part.text || '';

                // Extract citations if they exist
                if (part.annotations && part.annotations.length > 0) {
                  const citations = part.annotations
                    .filter((ann: any) => ann.type === 'file_citation')
                    .map((citation: any) => ({
                      filename: citation.filename,
                      file_id: citation.file_id,
                    }));

                  if (citations.length > 0) {
                    result.sources = citations;
                  }
                }
              }
            }
            // Extract content from response.output_text.done event
            else if (parsedData.type === 'response.output_text.done' && parsedData.text) {
              extractedContent = parsedData.text;
            }
            // Extract detailed results from response.output_item.done
            else if (parsedData.type === 'response.output_item.done' && parsedData.item) {
              const item = parsedData.item;
              if (item.content && item.content.length > 0) {
                const contentItem = item.content[0];
                if (contentItem.type === 'output_text') {
                  extractedContent = contentItem.text || '';

                  // Extract annotations if they exist
                  if (contentItem.annotations && contentItem.annotations.length > 0) {
                    const citations = contentItem.annotations
                      .filter((ann: any) => ann.type === 'file_citation')
                      .map((citation: any) => ({
                        filename: citation.filename,
                        file_id: citation.file_id,
                      }));

                    if (citations.length > 0) {
                      result.sources = citations;
                    }
                  }
                }
              }
            }
            // Process completed response to ensure we have full results
            else if (parsedData.type === 'response.completed' && parsedData.response) {
              const fullResponse = parsedData.response;

              // Extract sources from file search results if we have them
              if (fullResponse.output) {
                // Look for file_search_call output item
                const fileSearchOutput = fullResponse.output.find(
                  (item: any) => item.type === 'file_search_call' && item.results && item.results.length > 0,
                );

                if (fileSearchOutput && fileSearchOutput.results) {
                  // Map file search results to sources format
                  fileSearchResults = fileSearchOutput.results;
                  result.sources = fileSearchOutput.results.map((result: any) => ({
                    filename: result.filename,
                    file_id: result.file_id,
                    text: result.text,
                    score: result.score,
                  }));
                }

                // Look for message output item to get full content and annotations
                const messageOutput = fullResponse.output.find((item: any) => item.type === 'message');
                if (messageOutput && messageOutput.content && messageOutput.content.length > 0) {
                  const contentItem = messageOutput.content.find((item: any) => item.type === 'output_text');
                  if (contentItem) {
                    // Get the text content
                    if (contentItem.text) {
                      extractedContent = contentItem.text;
                    }

                    // Get annotations if they exist
                    if (contentItem.annotations && contentItem.annotations.length > 0) {
                      const citations = contentItem.annotations
                        .filter((ann: any) => ann.type === 'file_citation')
                        .map((citation: any) => ({
                          filename: citation.filename,
                          file_id: citation.file_id,
                        }));

                      if (citations.length > 0 && !result.sources) {
                        result.sources = citations;
                      }
                    }
                  }
                }
              }
            }
            // Handle legacy format (the final summary message)
            else if (parsedData.choices && parsedData.choices.length > 0) {
              if (parsedData.choices[0].message && parsedData.choices[0].message.content) {
                // Don't overwrite the main content we've already accumulated
                if (!extractedContent) {
                  extractedContent = parsedData.choices[0].message.content;
                }
              }
            }
          } catch (e) {
            console.warn('Failed to parse SSE data line:', data);
          }
        }
      }

      // Set final response text
      result.response = extractedContent;

      // Combine file search results with sources if we have them
      if (fileSearchResults.length > 0 && !result.sources) {
        result.sources = fileSearchResults.map((result: any) => ({
          filename: result.filename,
          file_id: result.file_id,
          text: result.text,
          score: result.score,
        }));
      }

      return result;
    }
  } else {
    // Regular JSON response
    return await response.json();
  }
}

export const actions = {
  // TODO: 1. Cross-App - Consider swapping over all callsites instead:
  ...crossAppActions,

  chemicals: {
    list() {
      return backendRequest('chemicalsList', {});
    },

    listCategories() {
      return backendRequest('chemicalCategoriesList', {});
    },

    listTriCategoryMappings() {
      return backendRequest('chemicalsToTriCategoriesFetch', {});
    },

    create(args: {
      tenantID: TenantID;
      casOrCategory: CasOrCategory;
      categories: CasOrCategory[];
      thresholds: { usageTypeID: UsageTypeID; pounds: number }[];
      name: string;
      isTRI: boolean;
      isCarcinogen: boolean;
      isSpecialConcern: boolean;
      isHap: boolean;
      isFlaggedForReview: boolean;
      deMinimis: number;
      notes: string;
    }) {
      return backendRequest('chemicalCreate', args);
    },

    createMissing(args: { tenantID: TenantID; casOrCategory: CasOrCategory; notes: string }) {
      return backendRequest('chemicalCreateMissing', args);
    },

    update(args: Omit<ChemicalBrief, 'isCategory'>) {
      return backendRequest('chemicalUpdate', args);
    },

    findUpdatesFromEpaSpreadsheet() {
      return backendRequest('chemicalsFindUpdatesFromEpaSpreadsheet', {});
    },

    syncWithEpaSpreadsheet() {
      return backendRequest('chemicalsSyncWithEpaSpreadsheet', {});
    },
  },

  facilities: {
    nucorReportsMappingGet(args: { facilityID: FacilityID }): Promise<{
      nucorReportsFacilityID: string | null;
    }> {
      return backendRequest('facilityNucorReportsMappingGet', args);
    },

    status(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('facilityStatus', args);
    },

    statusForAll(args: { tenantID: TenantID; year: number }) {
      return backendRequest('facilityStatusForAll', args);
    },

    async statusUpdate(args: {
      facilityID: FacilityID;
      year: number;
      isDoneAddingProducts?: boolean;
      isDoneDefiningActivity?: boolean;
      isDoneReviewingThresholds?: boolean;
      isDoneReviewingReleases?: boolean;
    }): Promise<void> {
      await backendRequest('facilityStatusToggle', args);
      facilityStatusUpdateTrigger.value += 1;
    },

    statusGapFillingModeUpdate(args: {
      facilityID: FacilityID;
      year: number;
      casOrCategory: CasOrCategory;
      sectionEightGapFillingMode: 'calculations' | 'productionRatio';
    }): Promise<Section8GapFillingModeRecord> {
      return backendRequest('facilityStatusGapFillingModeUpdate', args);
    },
  },

  threshold: {
    async chemicalsForFacility(args: {
      facilityID: FacilityID;
      year: number;
      isTRI: true | null;
      onlyExceedingThreshold: boolean;
    }): Promise<ThresholdBrief[]> {
      return (await backendRequest('thresholdChemicalsAndFormulaWarningsForFacility', args)).briefs;
    },

    chemicalsAndWarningsForFacility(args: {
      facilityID: FacilityID;
      year: number;
      isTRI: true | null;
      onlyExceedingThreshold: boolean;
    }): Promise<{ briefs: ThresholdBrief[]; warnings: Warning[] }> {
      return backendRequest('thresholdChemicalsAndFormulaWarningsForFacility', args);
    },
  },

  releaseProfiles: {
    get(releaseProfileID: ReleaseProfileID) {
      return backendRequest('releaseProfileGet', { releaseProfileID });
    },

    list(tenantID: TenantID, inventoryOnly = false) {
      return backendRequest('releaseProfilesList', { tenantID, inventoryOnly });
    },

    listForFacility(facilityID: FacilityID) {
      return backendRequest('releaseProfilesListForFacility', { facilityID });
    },

    create(
      tenantID: TenantID,
      config: {
        name: string;
        description: string;
        basis: '' | TRIQuantityBasisEstimationCodeType;
        productAssignmentGuidance: string;
        formula: ReleaseProfileFormula;
        formRLine: '' | FormRLeafLine;
        groupsInScope: FacilityGroupID[];
        facilitiesInScope: FacilityID[];
        emissionsComplianceMetricConfig: EmissionsComplianceMetricConfig | null;
      },
    ) {
      return backendRequest('releaseProfileSave', {
        ...config,
        tenantID,
        releaseProfileID: null,
      });
    },

    update(
      tenantID: TenantID,
      releaseProfileID: ReleaseProfileID,
      update: {
        name: string;
        description: string;
        productAssignmentGuidance: string;
        basis: '' | TRIQuantityBasisEstimationCodeType;
        formula: ReleaseProfileFormula;
        formRLine: '' | FormRLeafLine;
        groupsInScope: FacilityGroupID[];
        facilitiesInScope: FacilityID[];
        emissionsComplianceMetricConfig: EmissionsComplianceMetricConfig | null;
      },
    ) {
      return backendRequest('releaseProfileSave', {
        ...update,
        tenantID,
        releaseProfileID,
      });
    },

    setArchiveStatus(args: { releaseProfileID: ReleaseProfileID; isArchived: boolean }) {
      return backendRequest('releaseProfileSetArchiveStatus', args);
    },

    processRename(args: {
      facilityID: FacilityID;
      year: number;
      assignmentIds: ReleaseProfileAssignmentID[];
      processNameNew: string;
    }) {
      return backendRequest('releaseProfileProcessRename', args);
    },

    assignmentsUpdate(args: {
      facilityID: FacilityID;
      year: number;
      adding: ReleaseProfileAssignmentNew[];
      removing: ReleaseProfileAssignmentID[];
    }) {
      return backendRequest('releaseProfileAssignmentUpdate', args);
    },

    assignmentThresholdSettingsUpdate(args: {
      releaseProfileAssignmentID: ReleaseProfileAssignmentID;
      thresholdOrRelease?: 'release' | 'threshold' | 'both';
      thresholdUsageTypeID?: UsageTypeID | null;
    }) {
      return backendRequest('releaseProfileAssignmentThresholdSettingsUpdate', args);
    },

    assignmentChemicalExclusionUpdate(args: {
      releaseProfileAssignmentID: ReleaseProfileAssignmentID;
      casOrCategory: CasOrCategory;
      action: 'exclude' | 'include';
    }) {
      return backendRequest('releaseProfileProcessChemicalExclusionUpdate', args);
    },

    processesList(args: {
      facilityID: FacilityID;
      year: number;
      thresholdOrReleaseFilter: 'release' | 'threshold' | 'all';
    }) {
      return backendRequest('releaseProfileProcessesList', args);
    },

    async assignmentAttest(args: {
      facilityID: FacilityID;
      year: number;
      formRLine: FormRLeafLine;
      stateByChemical: ReleaseProfileAttestationState;
    }) {
      const result = await backendRequest('releaseProfileAssignmentAttest', args);
      facilityStatusUpdateTrigger.value += 1;
      return result;
    },

    variableCreate<
      T extends
        | ReleaseProfileConstant
        | ReleaseProfileQuestion
        | ReleaseProfileChemicalProfile
        | ReleaseProfileMixCalculation
        | ReleaseProfileUsageMix
        | ReleaseProfileWasteStream,
    >(variable: ReleaseProfileVariableForFrontendCreate<T>): Promise<T> {
      return backendRequest('releaseProfileVariableCreate', variable) as Promise<T>;
    },

    variableUpdate<
      T extends
        | ReleaseProfileConstant
        | ReleaseProfileQuestion
        | ReleaseProfileChemicalProfile
        | ReleaseProfileMixCalculation
        | ReleaseProfileUsageMix
        | ReleaseProfileWasteStream,
    >(variable: ReleaseProfileVariableForFrontendUpdate<T>): Promise<T> {
      return backendRequest('releaseProfileVariableUpdate', variable) as Promise<T>;
    },

    variableDelete(releaseProfileVariableID: ReleaseProfileVariableID) {
      return backendRequest('releaseProfileVariableDelete', { releaseProfileVariableID });
    },

    releaseProfileChoiceListsGet(args: { tenantID: TenantID; excludeReleaseProfileID: ReleaseProfileID }) {
      return backendRequest('releaseProfileChoiceListsGet', args);
    },

    questionConvertToWasteStream(args: { releaseProfileVariableID: ReleaseProfileVariableID }) {
      return backendRequest('releaseProfileQuestionConvertToWasteStream', args);
    },

    usageMixesCompute(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('releaseProfileUsageMixesCompute', args);
    },

    comparisonForTenant(args: { tenantID: TenantID; year: number; priorYear?: number }) {
      return backendRequest('releaseComparisonForTenant', args);
    },

    assignmentsYearOverYearForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('releaseProfileAssignmentsYearOverYearForTenant', args);
    },

    snapshotComparisonForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('releaseSnapshotComparisonForTenant', args);
    },

    assignmentAttestationsGet(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('releaseProfileAssignmentAttestationsGet', args);
    },

    didQuestionChangeAffectingOthers(args: {
      releaseProfileID: ReleaseProfileID;
      choiceListID: ReleaseProfileFormulaChoiceListID;
      question: string;
      choices: { choiceID: ReleaseProfileFormulaChoiceID; title: string }[];
    }) {
      return backendRequest('releaseProfileDidQuestionChangeAffectOthers', args);
    },

    usage(releaseProfileID: ReleaseProfileID) {
      return backendRequest('releaseProfileUsage', { releaseProfileID });
    },

    variableUsage(args: { tenantID: TenantID; nameForVariable: string }) {
      return backendRequest('releaseProfileVariableUsage', args);
    },
  },

  releaseProfileTemplates: {
    list(tenantID: TenantID | null) {
      return backendRequest('releaseProfileTemplatesList', { tenantID });
    },

    get(releaseProfileTemplateID: ReleaseProfileTemplateID) {
      return backendRequest('releaseProfileTemplateGet', { releaseProfileTemplateID });
    },

    promote(args: {
      releaseProfileID: ReleaseProfileID;
      tenantID: TenantID | null;
      name: string;
      description: string;
      category: string;
      tags: string[];
    }) {
      return backendRequest('releaseProfileTemplatePromote', args);
    },

    save(args: {
      releaseProfileTemplateID: ReleaseProfileTemplateID | null;
      tenantID: TenantID | null;
      name: string;
      description: string;
      productAssignmentGuidance: string;
      category: string;
      tags: string[];
      formRLine: '' | FormRLeafLine;
      basis: '' | TRIQuantityBasisEstimationCodeType;
      formula: ReleaseProfileFormula;
      variables: ReleaseProfileTemplateVariable[];
      emissionsComplianceMetricConfig: EmissionsComplianceMetricConfig | null;
    }) {
      return backendRequest('releaseProfileTemplateSave', args);
    },

    setArchiveStatus(args: { releaseProfileTemplateID: ReleaseProfileTemplateID; isArchived: boolean }) {
      return backendRequest('releaseProfileTemplateSetArchiveStatus', args);
    },

    delete(args: { releaseProfileTemplateID: ReleaseProfileTemplateID }) {
      return backendRequest('releaseProfileTemplateDelete', args);
    },

    checkConflicts(args: { releaseProfileTemplateID: ReleaseProfileTemplateID; tenantID: TenantID }) {
      return backendRequest('releaseProfileTemplateCheckConflicts', args);
    },

    import(args: {
      releaseProfileTemplateID: ReleaseProfileTemplateID;
      tenantID: TenantID;
      variableConflictResolutions: Record<string, 'reuse' | 'rename'>;
      variableOverrides?: Record<string, { description: string }>;
      usageMixProducts?: Record<string, ProductID[]>;
    }) {
      return backendRequest('releaseProfileTemplateImport', args);
    },
  },

  products: {
    dataSheetSet(args: TriBackendTypes['productDataSheetSet']['request']) {
      return backendRequest('productDataSheetSet', args);
    },
    detailsGet(args: { productID: ProductID }) {
      return backendRequest('productDetails', args);
    },

    briefGet(args: { facilityID: FacilityID; year: number; productID: ProductID }) {
      return backendRequest('productBriefGet', args);
    },

    listForFacility(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('productsListForFacility', args);
    },

    listForTenant(tenantID: TenantID) {
      return backendRequest('productsListForTenant', { tenantID });
    },

    productCreate(
      args: {
        tenantID: TenantID;
        name: string;
        specificGravity: number | '';
        flashPoint: number | null;
        flashPointUnit: 'fahrenheit' | 'celsius' | null;
        pH: number | null;
        chemicalProfileDataSourceID?: ChemicalProfileDataSourceID;
        chemicals: ChemicalEditing[];
        usageUnits: UsageUnits;
        solidsPercentageByWeight: number | null;
        solidsPercentageByVolume: number | null;
        tags: string[];
      } & Partial<HazardClassification>,
    ) {
      return backendRequest('productCreate', args);
    },

    productEdit(args: {
      productID: ProductID;
      // products table:
      updatedData: {
        name: string;
        specificGravity: number | '';
        flashPoint: number | null;
        flashPointUnit: 'fahrenheit' | 'celsius' | null;
        pH: number | null;
        usageUnits: UsageUnits;
        solidsPercentageByWeight: number | null;
        solidsPercentageByVolume: number | null;
        tags: string[];

        // chemicalProfileDataSources:
        chemicalProfileDataSourceID?: ChemicalProfileDataSourceID;

        // productChemicalComposition:
        chemicals: ChemicalEditing[];
      } & Partial<HazardClassification>;
    }) {
      return backendRequest('productEdit', args);
    },

    productUpdateSolids(args: {
      productID: ProductID;
      field: 'solidsPercentageByWeight' | 'solidsPercentageByVolume';
      value: number | null;
    }) {
      return backendRequest('productUpdateSolids', args);
    },

    productDelete(args: { productID: ProductID }) {
      return backendRequest('productDelete', args);
    },

    archiveToggle(args: { productID: ProductID; isArchived: boolean }) {
      return backendRequest('productArchiveToggle', args);
    },

    archivedList(tenantID: TenantID) {
      return backendRequest('productArchivedList', { tenantID });
    },

    tagsList(tenantID: TenantID): Promise<string[]> {
      return backendRequest('productTagsList', { tenantID });
    },

    recordUsage(args: {
      year: number;
      usage: {
        productID: ProductID;
        facilityID: FacilityID;
        manuallyEnteredValue: number;
        manuallyEnteredUnits: string;
      }[];
    }) {
      return backendRequest('recordUsage', args);
    },

    usageDelete(productUsageID: ProductUsageID) {
      return backendRequest('productUsageDelete', { productUsageID });
    },

    usageList(args: { productID: ProductID; facilityID: FacilityID; year: number }) {
      return backendRequest('productUsageList', args);
    },

    usageUnitsByProduct(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('usageUnitsByProduct', args);
    },

    productUsageYearOverYear(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('productUsageYearOverYear', args);
    },

    chemicalsListPotentialDeMinimis(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('productChemicalsListPotentialDeMinimis', args);
    },

    chemicalsListPotentialChemicalExemptions(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('productChemicalsListPotentialChemicalExemptions', args);
    },

    chemicalExemptionUpdate(args: {
      productChemicalCompositionID: ProductChemicalCompositionID;
      chemicalExemptionID: ChemicalExemptionID;
      decimalPlacesOfPrecision?: 1 | 2 | 3 | 4 | 5;
      mode: 'apply' | 'remove';
    }) {
      return backendRequest('productChemicalExemptionUpdate', args);
    },

    chemicalVolatileSolidsUpdate(args: {
      productChemicalCompositionID: ProductChemicalCompositionID;
      volatileOrSolids: '' | 'volatile' | 'solids';
    }) {
      return backendRequest('productChemicalVolatileSolidsUpdate', args);
    },

    productUsageYearOverYearForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('productUsageYearOverYearForTenant', args);
    },

    productExemptionsUpdate(args: {
      productID: ProductID;
      facilityID: FacilityID;
      productExemptionTypeID: ProductExemptionTypeID;
      notes: string;
      mode: 'apply' | 'remove';
    }) {
      return backendRequest('productExemptionUpdate', args);
    },

    productExemptionsList(facilityID: FacilityID) {
      return backendRequest('productExemptionsList', { facilityID });
    },

    productUsageYearOverYearDiffForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('productUsageYearOverYearDiffForTenant', args);
    },

    facilityProductPinToggle(args: { facilityID: FacilityID; productID: ProductID; pinned: boolean }) {
      return backendRequest('facilityProductPinToggle', args);
    },

    facilityProductPinsList(facilityID: FacilityID): Promise<ProductID[]> {
      return backendRequest('facilityProductPinsList', { facilityID });
    },

    pinsList(productID: ProductID) {
      return backendRequest('productPinsList', { productID });
    },

    productUsageMappingOverview(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('productUsageMappingOverview', args);
    },

    facilityUsageMappingList(args: {
      facilityID: FacilityID;
      dataSourceID: DataSourceID;
      year: number;
      facilityRawNames: string[];
    }) {
      return backendRequest('facilityUsageMappingList', args);
    },

    facilityUsageMappingSave(args: {
      facilityID: FacilityID;
      dataSourceID: DataSourceID;
      facilityRawNames: string[];
      products: {
        productRawName: string;
        productID: ProductID;
        unitsMappings: Partial<Record<string, string>>;
      }[];
    }) {
      return backendRequest('facilityUsageMappingSave', args);
    },

    facilityUsageMappingRefreshImported(args: {
      dataSourceID: DataSourceID;
      facilityID: FacilityID;
      year: number;
      requireCanonicalConversion?: boolean;
    }) {
      return backendRequest('facilityUsageMappingRefreshImported', args);
    },

    productUsageExcelFileConfigSave(args: {
      productUsageExcelFileConfigID: ProductUsageExcelFileConfigID | null;
      tenantID: TenantID;
      name: string;
      sheets: Partial<Record<string, ProductUsageSheetConfig>>;
    }): Promise<ProductUsageExcelFileConfigID> {
      return backendRequest('productUsageExcelFileConfigSave', args);
    },

    productUsageExcelFileConfigsList(args: { tenantID: TenantID }) {
      return backendRequest('productUsageExcelFileConfigsList', args);
    },

    productUsageForMappingImport(args: {
      tenantID: TenantID;
      dataSourceID: DataSourceID;
      name: string;
      year: number;
      rows: [
        facilityRawName: string,
        products: [
          productRawName: string,
          rows: [rowNum: number, additionalContext: Partial<Record<string, string>>, quantity: number, units: string][],
        ][],
      ][];
    }): Promise<DataSourceImportID> {
      return backendRequest('productUsageForMappingImport', args);
    },

    dataSourcesList(args: { tenantID: TenantID }) {
      return backendRequest('dataSourcesList', args);
    },

    dataSourceImportsListAll(args: { tenantID: TenantID }): Promise<
      {
        dataSourceImportID: DataSourceImportID;
        dataSourceID: DataSourceID;
        name: string;
        year: number;
        createdAt: string;
        dataSourceName: string;
        rowCount: number | null;
        linkedUsageCount: number | null;
      }[]
    > {
      return backendRequest('dataSourceImportsListAll', args);
    },

    dataSourceImportDelete(args: {
      tenantID: TenantID;
      dataSourceImportID: DataSourceImportID;
      deleteLinkedUsage: boolean;
    }) {
      return backendRequest('dataSourceImportDelete', args);
    },

    dataSourceCreate(args: { tenantID: TenantID; name: string }): Promise<DataSourceID> {
      return backendRequest('dataSourceCreate', args);
    },

    usageSummaryByFacilityAndYear(
      productID: ProductID,
    ): Promise<{ facilityID: FacilityID; facilityName: string; year: number; totalPoundsUsed: number }[]> {
      return backendRequest('productUsageSummaryByFacilityAndYear', { productID });
    },

    assignments(productID: ProductID): Promise<{
      emissionsUnits: {
        emissionsUnitTypeID: EmissionsUnitTypeID;
        emissionsUnitTypeName: string;
        units: {
          emissionsUnitID: EmissionsUnitID;
          facilityID: FacilityID;
          facilityName: string;
          name: string;
        }[];
      }[];
      releaseProfileAssignments: {
        facilityID: FacilityID;
        facilityName: string;
        year: number;
        releaseProfileID: ReleaseProfileID;
        releaseProfileName: string;
        processName: string;
      }[];
    }> {
      return backendRequest('productAssignments', { productID });
    },

    similar(productID: ProductID) {
      return backendRequest('productsSimilar', { productID });
    },

    combine(args: { sourceProductID: ProductID; targetProductID: ProductID }) {
      return backendRequest('productsCombine', args);
    },
  },

  sds: {
    parseSDS(args: {
      fileBase64: string;
      fileMimeType: string;
      facilityID: FacilityID;
      sdsUrl: string;
      aiProvider: 'gemini' | 'openai';
    }) {
      return backendRequest('sdsParse', args);
    },
  },

  chemicalCompositionAI: {
    parseComposition(args: {
      unstructuredText?: string;
      imagesBase64?: string[];
      fileMimeType?: string;
      facilityID?: FacilityID | null;
      tenantID?: TenantID;
      aiProvider: 'gemini' | 'openai';
      isTestReport?: boolean;
      additionalPrompt?: string;
    }) {
      return backendRequest('chemicalCompositionAIParse', args);
    },
  },

  chemicalProfileDataSources: {
    list: async (args: { tenantID: TenantID }) => {
      return await backendRequest('chemicalProfileDataSourcesList', args);
    },
    create: async (args: { tenantID: TenantID; name: string; description: string; fileID: FileID }) => {
      return await backendRequest('chemicalProfileDataSourceCreate', args);
    },
  },

  questionnaire: {
    get(args: { facilityID: FacilityID; year: number; thresholdProfilesOnly: boolean }) {
      return backendRequest('questionnaireGet', args);
    },

    answerUpdate(args: {
      facilityID: FacilityID;
      year: number;
      questionID: ReleaseProfileQuestionID;
      answer: number;
      productID?: ProductID;
    }) {
      return backendRequest('questionnaireAnswerUpdate', args);
    },

    answerNaUpdate(args: {
      facilityID: FacilityID;
      year: number;
      questionID: ReleaseProfileQuestionID;
      answer: 'N/A' | null;
    }) {
      return backendRequest('questionnaireAnswerNaUpdate', args);
    },

    formRVsFormAUpdate(args: {
      facilityID: FacilityID;
      year: number;
      casOrCategory: CasOrCategory;
      formRVsFormA: 'r' | 'a';
    }) {
      return backendRequest('questionnaireFormRVsFormAUpdate', args);
    },

    potwDistributionsKnownChemicalUpdate(args: {
      facilityID: FacilityID;
      year: number;
      casOrCategory: CasOrCategory;
      potwDistributionsKnownChemical: QuestionnairePotwDistributionsKnownChemical | null;
    }) {
      return backendRequest('questionnairePotwDistributionsKnownChemicalUpdate', args);
    },

    sourceDocumentationUpdate(args: {
      facilityID: FacilityID;
      year: number;
      questionID: ReleaseProfileQuestionID;
      sourceDocumentation: { description: string; files: number[] };
    }) {
      return backendRequest('questionnaireSourceDocumentationUpdate', args);
    },

    deviationExplanationUpdate(args: {
      facilityID: FacilityID;
      year: number;
      questionID: ReleaseProfileQuestionID;
      explanation: string;
    }) {
      return backendRequest('questionnaireDeviationExplanationUpdate', args);
    },

    answerDisplayUnitUpdate(args: {
      facilityID: FacilityID;
      year: number;
      questionID: ReleaseProfileQuestionID;
      displayUnit: string | null;
    }) {
      return backendRequest('questionnaireAnswerDisplayUnitUpdate', args);
    },

    releaseProfileFormulaChoiceUpdate(args: {
      facilityID: FacilityID;
      year: number;
      choiceListID: ReleaseProfileFormulaChoiceListID;
      choice: ReleaseProfileFormulaChoiceID;
    }) {
      return backendRequest('questionnaireReleaseProfileFormulaChoiceUpdate', args);
    },

    streamUpdate(args: {
      facilityID: FacilityID;
      year: number;
      streamID: StreamID;
      stream: QuestionnaireStream | null;
    }) {
      return backendRequest('questionnaireStreamUpdate', args);
    },

    potwUpdate(args: { facilityID: FacilityID; year: number; potwID: PotwID; potw: QuestionnairePotw | null }) {
      return backendRequest('questionnairePotwUpdate', args);
    },

    offSiteLocationUpdate(args: {
      facilityID: FacilityID;
      year: number;
      offSiteLocationID: OffSiteLocationID;
      offSiteLocation: QuestionnaireOffSiteLocation | null;
    }) {
      return backendRequest('questionnaireOffSiteLocationUpdate', args);
    },

    wasteTreatmentUpdate(args: {
      facilityID: FacilityID;
      year: number;
      wasteTreatment: QuestionnaireWasteTreatmentContainer;
    }) {
      return backendRequest('questionnaireWasteTreatmentUpdate', args);
    },

    anySignificantChangesUpdate(args: { facilityID: FacilityID; year: number; anySignificantChanges: string }) {
      return backendRequest('questionnaireAnySignificantChangesUpdate', args);
    },

    stateSpecificPlanningUpdate(args: { facilityID: FacilityID; year: number; stateSpecificPlanning: string }) {
      return backendRequest('questionnaireStateSpecificPlanningUpdate', args);
    },

    maxOnSiteAmountUpdate(args: { facilityID: FacilityID; year: number; maxOnSiteAmount: Section4MaxOnSiteAmount }) {
      return backendRequest('questionnaireMaxOnSiteAmountUpdate', args);
    },

    productionOrActivityRatioUpdate(args: {
      facilityID: FacilityID;
      year: number;
      productionOrActivityRatio: QuestionnaireProductionOrActivityRatio;
    }) {
      return backendRequest('questionnaireProductionOrActivityRatioUpdate', args);
    },

    sourceReductionActivitiesUpdate(args: {
      facilityID: FacilityID;
      year: number;
      sourceReductionActivities: QuestionnaireSourceReductionActivitiesContainer;
    }) {
      return backendRequest('questionnaireSourceReductionActivitiesUpdate', args);
    },

    onSiteEnergyRecoveryUpdate(args: {
      facilityID: FacilityID;
      year: number;
      onSiteEnergyRecovery: QuestionnaireOnSiteEnergyRecovery;
    }) {
      return backendRequest('questionnaireOnSiteEnergyRecoveryUpdate', args);
    },

    onSiteRecyclingUpdate(args: {
      facilityID: FacilityID;
      year: number;
      onSiteRecycling: QuestionnaireOnSiteRecycling;
    }) {
      return backendRequest('questionnaireOnSiteRecyclingUpdate', args);
    },

    chemicalActivitiesAndUsesUpdate(args: {
      facilityID: FacilityID;
      year: number;
      chemicalActivitiesAndUses: QuestionnaireChemicalActivitiesAndUsesContainer;
    }) {
      return backendRequest('questionnaireChemicalActivitiesAndUsesUpdate', args);
    },

    wasteTransferAnswerUpdate(args: {
      facilityID: FacilityID;
      year: number;
      transferID: TransferID;
      wasteTransfer: WasteTransfer | null;
    }) {
      return backendRequest('questionnaireWasteTransferAnswerUpdate', args);
    },

    statusForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('questionnaireThresholdAndReleaseStatusForTenant', args);
    },

    comparisonForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('questionnaireComparisonForTenant', args);
    },

    chemicalSpecificQuestionsForcedUpdate(args: {
      facilityID: FacilityID;
      year: number;
      questionID: ReleaseProfileQuestionID;
      chemicalsToAdd: CasOrCategory[];
      chemicalsToRemove: CasOrCategory[];
    }) {
      return backendRequest('chemicalSpecificQuestionsForcedUpdate', args);
    },

    facilityChemicalsForcedUpdate(args: {
      facilityID: FacilityID;
      year: number;
      chemicalsToAdd: { casOrCategory: CasOrCategory; mode: ChemicalForcedMode; usageTypeIds: UsageTypeID[] }[];
      chemicalsToRemove: CasOrCategory[];
    }) {
      return backendRequest('facilityChemicalsForcedUpdate', args);
    },

    variableExplore(args: {
      facilityID: FacilityID;
      year: number;
      baseQuestionID: ReleaseProfileQuestionID;
      questionID: ReleaseProfileQuestionID;
    }) {
      return backendRequest('questionnaireVariableExplore', args);
    },

    formulaVariablePopulatedBy(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('formulaVariablePopulatedBy', args) as Promise<
        Record<
          string,
          {
            populatedByEmail: string | null;
            populatedByName: string | null;
            populatedAt: string | null;
            where: string;
          }
        >
      >;
    },
  },

  receptors: {
    potwSearch(query: string) {
      return backendRequest('potwSearch', { query });
    },

    offSiteLocationSearch(query: string) {
      return backendRequest('offSiteLocationSearch', { query });
    },

    listForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('receptorsListForTenant', args);
    },
  },

  emails: {
    statusEmailSend(args: {
      recipient: Email;
      facilities: {
        facilityID: FacilityID;
        tenantID: TenantID;
        name: string;
        productsWithUsageNumerator: number;
        productsWithUsageDenominator: number;
        questionsAnswered: number;
        questionsTotal: number;
        questionnaireNecessary: boolean;
      }[];
    }) {
      return backendRequest('statusEmailSend', args);
    },
  },

  anomalies: {
    listForTenant(args: { tenantID: TenantID; year: number }) {
      return backendRequest('anomaliesListForTenant', args);
    },

    markAsReviewed(args: {
      facilityID: FacilityID;
      year: number;
      reviewedItemID: ProductID | ReleaseProfileQuestionID;
      type: 'usage' | 'question';
      notes: string;
      context: ProductUsageComparisonItem | QuestionnaireComparisonItem;
    }) {
      return backendRequest('anomalyMarkAsReviewed', args);
    },

    removeReview(args: { facilityID: FacilityID; year: number; reviewedItemID: ProductID | ReleaseProfileQuestionID }) {
      return backendRequest('anomalyRemoveReview', args);
    },
  },

  ai: {
    askQuestion: askQuestionFromVectorStore,
    aiPickFontAwesomeIcon(subject: string) {
      return backendRequest('aiPickFontAwesomeIcon', { subject });
    },
  },

  formR: {
    xmlFormRecordDownload(args: {
      facilityID: FacilityID;
      year: number;
      inputs: FormRInputsSnapshot;
      xmlGenerated: string;
    }) {
      return backendRequest('xmlFormRecordDownload', args);
    },

    async submissionStatusUpdate(args: {
      facilityID: FacilityID;
      year: number;
      formRSubmissionStatus: FormRSubmissionStatus;
    }): Promise<void> {
      await backendRequest('facilityFormRSubmissionStatusUpdate', args);
      facilityStatusUpdateTrigger.value += 1;
    },

    facilityExportsList(args: { facilityID: FacilityID; year: number }) {
      return backendRequest('facilityFormRExportsList', args);
    },
  },

  emissions: {
    async limitGet(facilityID: FacilityID, emissionsLimitID: EmissionsLimitID) {
      const result = await backendRequest('emissionsLimitsList', { facilityID, emissionsLimitID });
      return result[0] ?? null;
    },

    limitsList(facilityID: FacilityID) {
      return backendRequest('emissionsLimitsList', { facilityID, emissionsLimitID: null });
    },

    limitSave(args: {
      emissionsLimitID: EmissionsLimitID | null;
      facilityID: FacilityID;
      name: string;
      reference: string;
      emissionsUnitIDs: EmissionsUnitID[];
      pollutantSelector: EmissionsLimitPollutantSelector;
      chemicalGrouping: EmissionsLimitChemicalGrouping;
      unitGrouping: EmissionsLimitUnitGrouping;
      limitType: EmissionsLimitType;
      window: EmissionsLimitWindow;
      windowReduction: EmissionsLimitWindowReduction;
      effectiveStartDate: ISO8601DateOnly;
      limitValue: number;
      limitUnit: string;
      pollutantLimits: EmissionsPollutantLimit[];
      releaseProfileID: ReleaseProfileID | null;
      emissionsBasis: EmissionsBasis;
      potentialBasisPeriod: PotentialEmissionsBasisPeriod | null;
      potentialOutputSelections: PotentialEmissionsOutputSelection[];
    }) {
      return backendRequest('emissionsLimitSave', args) as Promise<EmissionsLimitID>;
    },

    limitDelete(args: { emissionsLimitID: EmissionsLimitID; facilityID: FacilityID }) {
      return backendRequest('emissionsLimitDelete', args) as Promise<void>;
    },

    unitTypesList(tenantID: TenantID) {
      return backendRequest('emissionsUnitTypesList', { tenantID });
    },

    unitTypeSave(args: {
      emissionsUnitTypeID: null | EmissionsUnitTypeID;
      tenantID: TenantID;
      name: string;
      icon: string;
      description: string;
      releaseProfileIds: ReleaseProfileID[];
      variableConfigs: {
        releaseProfileVariableID: ReleaseProfileVariableID;
        casOrCategory: CasOrCategory | null;
        frequency: EmissionsVariableAnswerFrequency;
        answer: number | null;
      }[];
      formulaChoiceConfigs: {
        choiceListID: ReleaseProfileFormulaChoiceListID;
        frequency: EmissionsVariableAnswerFrequency;
        answer: ReleaseProfileFormulaChoiceID | null;
      }[];
      predefinedVariableConfigs: {
        predefinedVariableName: string;
        frequency: EmissionsVariableAnswerFrequency;
      }[];
      importedFromTriFacilityID: FacilityID | null;
      importedFromTriProcessName: string | null;
      deletedAt: ISO8601Timestamp | null;
      deletedBy: UserID | null;
    }) {
      return backendRequest('emissionsUnitTypeSave', args);
    },

    unitsList(facilityID: FacilityID) {
      return backendRequest('emissionsUnitsList', { facilityID });
    },

    unitsReorder(facilityID: FacilityID, orderedIDs: EmissionsUnitID[]) {
      return backendRequest('emissionsUnitsReorder', { facilityID, orderedIDs });
    },

    unitSave(args: {
      emissionsUnitID: null | EmissionsUnitID;
      facilityID: FacilityID;
      emissionsUnitTypeID: EmissionsUnitTypeID;
      name: string;
      trackingFrequency: EmissionsUnit['trackingFrequency'];
      trackingMode: EmissionsUnit['trackingMode'];
      entriesStartAt: ISO8601DateOnly;
      products: { productID: ProductID; archivedAt: ISO8601DateOnly | null }[];
      variableConfigs: {
        releaseProfileVariableID: ReleaseProfileVariableID;
        casOrCategory: CasOrCategory | null;
        answer: number | null;
      }[];
      formulaChoiceConfigs: {
        choiceListID: ReleaseProfileFormulaChoiceListID;
        answer: ReleaseProfileFormulaChoiceID;
      }[];
      deletedAt: ISO8601Timestamp | null;
      deletedBy: UserID | null;
    }) {
      return backendRequest('emissionsUnitSave', args);
    },

    unitSaveViewPrefs(args: {
      emissionsUnitID: EmissionsUnitID;
      viewPeriodSort: 'asc' | 'desc';
      viewTransposed: boolean;
      viewPeriodsPerPage: number | null;
      viewHighlightGaps: boolean;
    }) {
      return backendRequest('emissionsUnitSaveViewPrefs', args);
    },

    answersList(emissionsUnitID: EmissionsUnitID) {
      return backendRequest('emissionsUnitAnswersList', { emissionsUnitID });
    },

    facilityAnswersList(facilityID: FacilityID) {
      return backendRequest('emissionsFacilityAnswersList', { facilityID });
    },

    unitTypeAnswersList(emissionsUnitTypeID: EmissionsUnitTypeID) {
      return backendRequest('emissionsUnitTypeAnswersList', { emissionsUnitTypeID });
    },

    answerSave(
      args: {
        period: EmissionsPeriod | 'potential';
        questionID: ReleaseProfileQuestionID;
        answer: number | ReleaseProfileFormulaChoiceID | 'N/A' | null;
      } & (
        | {
            emissionsUnitID: EmissionsUnitID;
          }
        | { emissionsUnitTypeID: EmissionsUnitTypeID }
        | { facilityID: FacilityID }
      ),
    ) {
      if ('emissionsUnitID' in args) {
        return backendRequest('emissionsUnitAnswerSave', args);
      } else if ('emissionsUnitTypeID' in args) {
        return backendRequest('emissionsUnitTypeAnswerSave', args);
      } else {
        return backendRequest('emissionsFacilityAnswerSave', args);
      }
    },

    reportsList(emissionsUnitTypeID: EmissionsUnitTypeID) {
      return backendRequest('emissionsReportsList', { emissionsUnitTypeID });
    },

    reportSave(args: {
      emissionsReportID: EmissionsReportID | null;
      emissionsUnitTypeID: EmissionsUnitTypeID;
      name: string;
      pipelineOperators: PipelineOperatorAnnotated[];
      isArchived: boolean;
    }) {
      return backendRequest('emissionsReportSave', args);
    },

    periodSplitsList(emissionsUnitID: EmissionsUnitID) {
      return backendRequest('emissionsUnitPeriodSplitsList', { emissionsUnitID });
    },

    periodSplitSave(args: {
      emissionsUnitID: EmissionsUnitID;
      period: EmissionsPeriod;
      splitDates: ISO8601DateOnly[];
    }) {
      return backendRequest('emissionsUnitPeriodSplitSave', args);
    },

    facilityUnitsCompletionList(facilityID: FacilityID) {
      return backendRequest('emissionsFacilityUnitsCompletionList', { facilityID });
    },
  },

  tierii: {
    tierIIInventoryProductSelections(args: TriBackendTypes['tierIIInventoryProductSelections']['request']) {
      return backendRequest('tierIIInventoryProductSelections', args);
    },
    tierIIInventoryProductInclusionSet(args: TriBackendTypes['tierIIInventoryProductInclusionSet']['request']) {
      return backendRequest('tierIIInventoryProductInclusionSet', args);
    },
    tierIIChemicalBasisClear(args: TriBackendTypes['tierIIChemicalBasisClear']['request']) {
      return backendRequest('tierIIChemicalBasisClear', args);
    },
    tierIIChemicalBasisSave(args: TriBackendTypes['tierIIChemicalBasisSave']['request']) {
      return backendRequest('tierIIChemicalBasisSave', args);
    },
    tierIIThresholdReview(args: TriBackendTypes['tierIIThresholdReview']['request']) {
      return backendRequest('tierIIThresholdReview', args);
    },
    tierIIStateThresholdReviewSave(args: TriBackendTypes['tierIIStateThresholdReviewSave']['request']) {
      return backendRequest('tierIIStateThresholdReviewSave', args);
    },
    tierIIChemicalCandidatesList(args: TriBackendTypes['tierIIChemicalCandidatesList']['request']) {
      return backendRequest('tierIIChemicalCandidatesList', args);
    },
    tierIIChemicalExemptionClear(args: TriBackendTypes['tierIIChemicalExemptionClear']['request']) {
      return backendRequest('tierIIChemicalExemptionClear', args);
    },
    tierIIChemicalExemptionSave(args: TriBackendTypes['tierIIChemicalExemptionSave']['request']) {
      return backendRequest('tierIIChemicalExemptionSave', args);
    },
    tierIIFacilityIdentificationSave(args: TriBackendTypes['tierIIFacilityIdentificationSave']['request']) {
      return backendRequest('tierIIFacilityIdentificationSave', args);
    },
    tierIIStateCorrectionReviewConfirm(args: TriBackendTypes['tierIIStateCorrectionReviewConfirm']['request']) {
      return backendRequest('tierIIStateCorrectionReviewConfirm', args);
    },
    tierIIFacilityInfoGet(args: TriBackendTypes['tierIIFacilityInfoGet']['request']) {
      return backendRequest('tierIIFacilityInfoGet', args);
    },
    tierIIFacilityInfoSave(args: TriBackendTypes['tierIIFacilityInfoSave']['request']) {
      return backendRequest('tierIIFacilityInfoSave', args);
    },
    tierIIFormDataSave(args: TriBackendTypes['tierIIFormDataSave']['request']) {
      return backendRequest('tierIIFormDataSave', args);
    },
    tierIIInventoryComputeFromProfile(args: TriBackendTypes['tierIIInventoryComputeFromProfile']['request']) {
      return backendRequest('tierIIInventoryComputeFromProfile', args);
    },
    tierIIInventoryList(args: TriBackendTypes['tierIIInventoryList']['request']) {
      return backendRequest('tierIIInventoryList', args);
    },
    tierIIInventoryQuestionnaireGet(args: TriBackendTypes['tierIIInventoryQuestionnaireGet']['request']) {
      return backendRequest('tierIIInventoryQuestionnaireGet', args);
    },
    tierIIProductSpecificGravitySave(args: TriBackendTypes['tierIIProductSpecificGravitySave']['request']) {
      return backendRequest('tierIIProductSpecificGravitySave', args);
    },
    tierIIProductExemptionClear(args: TriBackendTypes['tierIIProductExemptionClear']['request']) {
      return backendRequest('tierIIProductExemptionClear', args);
    },
    tierIIProductExemptionSave(args: TriBackendTypes['tierIIProductExemptionSave']['request']) {
      return backendRequest('tierIIProductExemptionSave', args);
    },
    tierIIProductReportingOverrideSave(args: TriBackendTypes['tierIIProductReportingOverrideSave']['request']) {
      return backendRequest('tierIIProductReportingOverrideSave', args);
    },
    tierIIEhsQualifierSave(args: TriBackendTypes['tierIIEhsQualifierSave']['request']) {
      return backendRequest('tierIIEhsQualifierSave', args);
    },
    tierIIInventorySave(args: TriBackendTypes['tierIIInventorySave']['request']) {
      return backendRequest('tierIIInventorySave', args);
    },
    tierIIInventorySnapshotDelete(args: TriBackendTypes['tierIIInventorySnapshotDelete']['request']) {
      return backendRequest('tierIIInventorySnapshotDelete', args);
    },
    tierIIInventorySnapshotsImport(args: TriBackendTypes['tierIIInventorySnapshotsImport']['request']) {
      return backendRequest('tierIIInventorySnapshotsImport', args);
    },
    tierIIInventorySnapshotsPreview(args: TriBackendTypes['tierIIInventorySnapshotsPreview']['request']) {
      return backendRequest('tierIIInventorySnapshotsPreview', args);
    },
    tierIIInventorySnapshotSave(args: TriBackendTypes['tierIIInventorySnapshotSave']['request']) {
      return backendRequest('tierIIInventorySnapshotSave', args);
    },
    tierIIStorageLocationsList(args: TriBackendTypes['tierIIStorageLocationsList']['request']) {
      return backendRequest('tierIIStorageLocationsList', args);
    },
    tierIIStorageLocationSave(args: TriBackendTypes['tierIIStorageLocationSave']['request']) {
      return backendRequest('tierIIStorageLocationSave', args);
    },
    tierIIInventoryVesselsSave(args: TriBackendTypes['tierIIInventoryVesselsSave']['request']) {
      return backendRequest('tierIIInventoryVesselsSave', args);
    },
    tierIIPubchemImport(args: TriBackendTypes['tierIIPubchemImport']['request']) {
      return backendRequest('tierIIPubchemImport', args);
    },
    tierIIPubchemImportMixture(args: TriBackendTypes['tierIIPubchemImportMixture']['request']) {
      return backendRequest('tierIIPubchemImportMixture', args);
    },
    tierIIReleaseProfilesListForFacility(args: TriBackendTypes['tierIIReleaseProfilesListForFacility']['request']) {
      return backendRequest('tierIIReleaseProfilesListForFacility', args);
    },
    tierIIReparseSdsHazards(args: TriBackendTypes['tierIIReparseSdsHazards']['request']) {
      return backendRequest('tierIIReparseSdsHazards', args);
    },
    tierIIReportFormList(args: TriBackendTypes['tierIIReportFormList']['request']) {
      return backendRequest('tierIIReportFormList', args);
    },
    tierIIReviewStatusUpdate(args: TriBackendTypes['tierIIReviewStatusUpdate']['request']) {
      return backendRequest('tierIIReviewStatusUpdate', args);
    },
    tierIISiteMapExportPdf(args: TriBackendTypes['tierIISiteMapExportPdf']['request']) {
      return backendRequest('tierIISiteMapExportPdf', args);
    },
    tierIISiteMapGeocodeFacility(args: TriBackendTypes['tierIISiteMapGeocodeFacility']['request']) {
      return backendRequest('tierIISiteMapGeocodeFacility', args);
    },
    tierIISiteMapGet(args: TriBackendTypes['tierIISiteMapGet']['request']) {
      return backendRequest('tierIISiteMapGet', args);
    },
    tierIISiteMapGetLatestForFacility(args: TriBackendTypes['tierIISiteMapGetLatestForFacility']['request']) {
      return backendRequest('tierIISiteMapGetLatestForFacility', args);
    },
    tierIISiteMapRequirementsGet(args: TriBackendTypes['tierIISiteMapRequirementsGet']['request']) {
      return backendRequest('tierIISiteMapRequirementsGet', args);
    },
    tierIISiteMapSave(args: TriBackendTypes['tierIISiteMapSave']['request']) {
      return backendRequest('tierIISiteMapSave', args);
    },
    tierIIStatusForAll(args: TriBackendTypes['tierIIStatusForAll']['request']) {
      return backendRequest('tierIIStatusForAll', args);
    },
    tierIIReportReviewGet(args: TriBackendTypes['tierIIReportReviewGet']['request']) {
      return backendRequest('tierIIReportReviewGet', args);
    },
    tierIIStateReportingContextGet(args: TriBackendTypes['tierIIStateReportingContextGet']['request']) {
      return backendRequest('tierIIStateReportingContextGet', args);
    },
    tierIIDraftPackageGet(args: TriBackendTypes['tierIIDraftPackageGet']['request']) {
      return backendRequest('tierIIDraftPackageGet', args);
    },
    tierIISubmissionPackageGet(args: TriBackendTypes['tierIISubmissionPackageGet']['request']) {
      return backendRequest('tierIISubmissionPackageGet', args);
    },
    tierIISubmissionStatusGet(args: TriBackendTypes['tierIISubmissionStatusGet']['request']) {
      return backendRequest('tierIISubmissionStatusGet', args);
    },
    tierIISubmissionStatusUpdate(args: TriBackendTypes['tierIISubmissionStatusUpdate']['request']) {
      return backendRequest('tierIISubmissionStatusUpdate', args);
    },
    tierIIJurisdictionList(args: TriBackendTypes['tierIIJurisdictionList']['request']) {
      return backendRequest('tierIIJurisdictionList', args);
    },
    tierIIJurisdictionProfileGet(args: TriBackendTypes['tierIIJurisdictionProfileGet']['request']) {
      return backendRequest('tierIIJurisdictionProfileGet', args);
    },
    tierIIJurisdictionProfileSave(args: TriBackendTypes['tierIIJurisdictionProfileSave']['request']) {
      return backendRequest('tierIIJurisdictionProfileSave', args);
    },
    tierIIJurisdictionThresholdRuleSave(args: TriBackendTypes['tierIIJurisdictionThresholdRuleSave']['request']) {
      return backendRequest('tierIIJurisdictionThresholdRuleSave', args);
    },
    tierIIJurisdictionThresholdRuleDelete(args: TriBackendTypes['tierIIJurisdictionThresholdRuleDelete']['request']) {
      return backendRequest('tierIIJurisdictionThresholdRuleDelete', args);
    },
  },

  files: {
    create(args: {
      tenantID: TenantID;
      facilityID: FacilityID | null;
      name: string;
      contentType: string;
      sizeInBytes: number;
      firebaseStoragePath: string;
      createdFor: unknown;
    }) {
      return backendRequest('fileCreate', args);
    },
    list(args: { tenantID: TenantID; facilityID: FacilityID | null }) {
      return backendRequest('fileList', args);
    },
    get(args: { fileID: FileID }) {
      return backendRequest('fileGet', args);
    },
    deleteUnattachedStandardShipmentUpload(args: { facilityID: FacilityID; fileID: FileID }) {
      return backendRequest('fileDeleteUnattachedStandardShipmentUpload', args);
    },
  },

  raffle: {
    submitEntry(args: {
      facilityName: string;
      personName: string;
      email: Email;
      notes: string;
      fileName: string;
      fileType: string;
      firebaseStoragePath: string;
    }) {
      return backendAnonymousRequest('raffleSubmitEntry', args);
    },
  },

  compliance: {
    facilityConfigGet(args: { facilityID: FacilityID }) {
      return complianceBackendRequest('facilityConfigGet', args);
    },
    facilityConfigUpdate(facilityConfig: { facilityID: FacilityID; timeZone: string }) {
      return complianceBackendRequest('facilityUpdate', { facilityConfig });
    },
    hazWasteContainerInspectionTaskGet(args: { tenantID: TenantID; facilityID: FacilityID }) {
      return complianceBackendRequest('hazWasteContainerInspectionTaskGet', args);
    },
    hazWasteContainerInspectionTaskEnsure(args: { tenantID: TenantID; facilityID: FacilityID }) {
      return complianceBackendRequest('hazWasteContainerInspectionTaskEnsure', args);
    },
    hazWasteStreamReviewPolicyEnsure(args: { tenantID: TenantID; facilityID: FacilityID; intervalMonths: number }) {
      return complianceBackendRequest('hazWasteStreamReviewPolicyEnsure', args);
    },
    taskCustomFieldsUpdate(args: ComplianceBackendTypes['taskCustomFieldsUpdate']['request']) {
      return complianceBackendRequest('taskCustomFieldsUpdate', args);
    },
    taskComplete(args: Omit<ComplianceBackendTypes['taskComplete']['request'], 'correctiveActions'>) {
      return complianceBackendRequest('taskComplete', { ...args, correctiveActions: [] });
    },
    taskUncomplete(args: ComplianceBackendTypes['taskUncomplete']['request']) {
      return complianceBackendRequest('taskUncomplete', args);
    },
  },

  hazwaste: {
    hazWasteExistingDeterminationIvyReview(
      args: HazWasteBackendTypes['hazWasteExistingDeterminationIvyReview']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteExistingDeterminationIvyReview', args);
    },
    hazWasteTestReportIvyReview(args: HazWasteBackendTypes['hazWasteTestReportIvyReview']['request']) {
      return hazWasteBackendRequest('hazWasteTestReportIvyReview', args);
    },
    trainingFacilityQuestionnaireGet(args: HazWasteBackendTypes['trainingFacilityQuestionnaireGet']['request']) {
      return hazWasteBackendRequest('trainingFacilityQuestionnaireGet', args);
    },
    trainingFacilityQuestionnaireAnswerSave(
      args: HazWasteBackendTypes['trainingFacilityQuestionnaireAnswerSave']['request'],
    ) {
      return hazWasteBackendRequest('trainingFacilityQuestionnaireAnswerSave', args);
    },
    trainingFacilityQuestionnaireContingencyPlanAttach(
      args: HazWasteBackendTypes['trainingFacilityQuestionnaireContingencyPlanAttach']['request'],
    ) {
      return hazWasteBackendRequest('trainingFacilityQuestionnaireContingencyPlanAttach', args);
    },
    trainingFacilityQuestionnaireIvySuggest(
      args: HazWasteBackendTypes['trainingFacilityQuestionnaireIvySuggest']['request'],
    ) {
      return hazWasteBackendRequest('trainingFacilityQuestionnaireIvySuggest', args);
    },
    trainingRequirementsList(args: HazWasteBackendTypes['trainingRequirementsList']['request']) {
      return hazWasteBackendRequest('trainingRequirementsList', args);
    },
    trainingRequirementSaveDraft(args: HazWasteBackendTypes['trainingRequirementSaveDraft']['request']) {
      return hazWasteBackendRequest('trainingRequirementSaveDraft', args);
    },
    trainingProfilesList(args: HazWasteBackendTypes['trainingProfilesList']['request']) {
      return hazWasteBackendRequest('trainingProfilesList', args);
    },
    trainingProfileSaveDraft(args: HazWasteBackendTypes['trainingProfileSaveDraft']['request']) {
      return hazWasteBackendRequest('trainingProfileSaveDraft', args);
    },
    trainingProfilePublish(args: HazWasteBackendTypes['trainingProfilePublish']['request']) {
      return hazWasteBackendRequest('trainingProfilePublish', args);
    },
    trainingProfileRevisionApply(args: HazWasteBackendTypes['trainingProfileRevisionApply']['request']) {
      return hazWasteBackendRequest('trainingProfileRevisionApply', args);
    },
    trainingPersonnelAssignmentsList(args: HazWasteBackendTypes['trainingPersonnelAssignmentsList']['request']) {
      return hazWasteBackendRequest('trainingPersonnelAssignmentsList', args);
    },
    trainingMyPersonnelAssignment(args: HazWasteBackendTypes['trainingMyPersonnelAssignment']['request']) {
      return hazWasteBackendRequest('trainingMyPersonnelAssignment', args);
    },
    trainingPersonnelList(args: HazWasteBackendTypes['trainingPersonnelList']['request']) {
      return hazWasteBackendRequest('trainingPersonnelList', args);
    },
    trainingPersonnelAssignmentSave(args: HazWasteBackendTypes['trainingPersonnelAssignmentSave']['request']) {
      return hazWasteBackendRequest('trainingPersonnelAssignmentSave', args);
    },
    hazWasteProfileGet(args: HazWasteBackendTypes['hazWasteProfileGet']['request']) {
      return hazWasteBackendRequest('hazWasteProfileGet', args);
    },
    hazWasteProfileSave(args: HazWasteBackendTypes['hazWasteProfileSave']['request']) {
      return hazWasteBackendRequest('hazWasteProfileSave', args);
    },
    materialProfilesList(args: HazWasteBackendTypes['materialProfilesList']['request']) {
      return hazWasteBackendRequest('materialProfilesList', args);
    },
    materialProfileAggregateSave(args: HazWasteBackendTypes['materialProfileAggregateSave']['request']) {
      return hazWasteBackendRequest('materialProfileAggregateSave', args);
    },
    materialProfileArchive(args: HazWasteBackendTypes['materialProfileArchive']['request']) {
      return hazWasteBackendRequest('materialProfileArchive', args);
    },
    materialProfileReclassifyAsHazardous(
      args: HazWasteBackendTypes['materialProfileReclassifyAsHazardous']['request'],
    ) {
      return hazWasteBackendRequest('materialProfileReclassifyAsHazardous', args);
    },
    materialProfileVesselAssignmentsList(
      args: HazWasteBackendTypes['materialProfileVesselAssignmentsList']['request'],
    ) {
      return hazWasteBackendRequest('materialProfileVesselAssignmentsList', args);
    },
    materialProfileVesselAssign(args: HazWasteBackendTypes['materialProfileVesselAssign']['request']) {
      return hazWasteBackendRequest('materialProfileVesselAssign', args);
    },
    materialProfileVesselAccumulationStartDateSave(
      args: HazWasteBackendTypes['materialProfileVesselAccumulationStartDateSave']['request'],
    ) {
      return hazWasteBackendRequest('materialProfileVesselAccumulationStartDateSave', args);
    },
    materialProfileVesselAccumulationHistoryList(
      args: HazWasteBackendTypes['materialProfileVesselAccumulationHistoryList']['request'],
    ) {
      return hazWasteBackendRequest('materialProfileVesselAccumulationHistoryList', args);
    },
    usedOilMaterialDetailGet(args: HazWasteBackendTypes['usedOilMaterialDetailGet']['request']) {
      return hazWasteBackendRequest('usedOilMaterialDetailGet', args);
    },
    universalWasteMaterialDetailGet(args: HazWasteBackendTypes['universalWasteMaterialDetailGet']['request']) {
      return hazWasteBackendRequest('universalWasteMaterialDetailGet', args);
    },
    nonHazardousWasteMaterialDetailGet(args: HazWasteBackendTypes['nonHazardousWasteMaterialDetailGet']['request']) {
      return hazWasteBackendRequest('nonHazardousWasteMaterialDetailGet', args);
    },
    rcraExemptMaterialClaimsList(args: HazWasteBackendTypes['rcraExemptMaterialClaimsList']['request']) {
      return hazWasteBackendRequest('rcraExemptMaterialClaimsList', args);
    },
    hazWasteStreamsList(args: HazWasteBackendTypes['hazWasteStreamsList']['request']) {
      return hazWasteBackendRequest('hazWasteStreamsList', args);
    },
    hazWasteStreamSave(args: HazWasteBackendTypes['hazWasteStreamSave']['request']) {
      return hazWasteBackendRequest('hazWasteStreamSave', args);
    },
    hazWasteStreamReclassifyToProgram(args: HazWasteBackendTypes['hazWasteStreamReclassifyToProgram']['request']) {
      return hazWasteBackendRequest('hazWasteStreamReclassifyToProgram', args);
    },
    hazWasteStreamArchive(args: HazWasteBackendTypes['hazWasteStreamArchive']['request']) {
      return hazWasteBackendRequest('hazWasteStreamArchive', args);
    },
    hazWasteStreamBiennialFieldsSave(args: HazWasteBackendTypes['hazWasteStreamBiennialFieldsSave']['request']) {
      return hazWasteBackendRequest('hazWasteStreamBiennialFieldsSave', args);
    },
    hazWasteContainerAssignmentsList(args: HazWasteBackendTypes['hazWasteContainerAssignmentsList']['request']) {
      return hazWasteBackendRequest('hazWasteContainerAssignmentsList', args);
    },
    hazWasteAccumulationAreaSave(args: HazWasteBackendTypes['hazWasteAccumulationAreaSave']['request']) {
      return hazWasteBackendRequest('hazWasteAccumulationAreaSave', args);
    },
    hazWasteContainerStreamAssign(args: HazWasteBackendTypes['hazWasteContainerStreamAssign']['request']) {
      return hazWasteBackendRequest('hazWasteContainerStreamAssign', args);
    },
    hazWasteContainerAccumulationStartDateSave(
      args: HazWasteBackendTypes['hazWasteContainerAccumulationStartDateSave']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteContainerAccumulationStartDateSave', args);
    },
    hazWasteContainerActivate(args: HazWasteBackendTypes['hazWasteContainerActivate']['request']) {
      return hazWasteBackendRequest('hazWasteContainerActivate', args);
    },
    hazWasteContainerMarkShipped(args: HazWasteBackendTypes['hazWasteContainerMarkShipped']['request']) {
      return hazWasteBackendRequest('hazWasteContainerMarkShipped', args);
    },
    hazWasteContainerRetire(args: HazWasteBackendTypes['hazWasteContainerRetire']['request']) {
      return hazWasteBackendRequest('hazWasteContainerRetire', args);
    },
    hazWasteContainerReplaceWithIdentical(
      args: HazWasteBackendTypes['hazWasteContainerReplaceWithIdentical']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteContainerReplaceWithIdentical', args);
    },
    hazWasteContainerFieldCardGet(args: HazWasteBackendTypes['hazWasteContainerFieldCardGet']['request']) {
      return hazWasteBackendRequest('hazWasteContainerFieldCardGet', args);
    },
    hazWasteContainerLifecycleHistoryList(
      args: HazWasteBackendTypes['hazWasteContainerLifecycleHistoryList']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteContainerLifecycleHistoryList', args);
    },
    hazWasteAccumulationAreaContainerLifecycleList(
      args: HazWasteBackendTypes['hazWasteAccumulationAreaContainerLifecycleList']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteAccumulationAreaContainerLifecycleList', args);
    },
    hazWasteContainerLabelsListEligible(args: HazWasteBackendTypes['hazWasteContainerLabelsListEligible']['request']) {
      return hazWasteBackendRequest('hazWasteContainerLabelsListEligible', args);
    },
    hazWasteAccumulationDeadlineTasksSync(
      args: HazWasteBackendTypes['hazWasteAccumulationDeadlineTasksSync']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteAccumulationDeadlineTasksSync', args);
    },
    hazWasteContainerLabelsExportPdf(args: HazWasteBackendTypes['hazWasteContainerLabelsExportPdf']['request']) {
      return hazWasteBackendRequest('hazWasteContainerLabelsExportPdf', args);
    },
    hazWasteGenerationLogsList(args: HazWasteBackendTypes['hazWasteGenerationLogsList']['request']) {
      return hazWasteBackendRequest('hazWasteGenerationLogsList', args);
    },
    hazWasteMonthlyGeneratorAdjustmentsList(
      args: HazWasteBackendTypes['hazWasteMonthlyGeneratorAdjustmentsList']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteMonthlyGeneratorAdjustmentsList', args);
    },
    hazWasteMonthlyGeneratorAdjustmentSave(
      args: HazWasteBackendTypes['hazWasteMonthlyGeneratorAdjustmentSave']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteMonthlyGeneratorAdjustmentSave', args);
    },
    hazWasteGenerationLogSave(args: HazWasteBackendTypes['hazWasteGenerationLogSave']['request']) {
      return hazWasteBackendRequest('hazWasteGenerationLogSave', args);
    },
    hazWasteGenerationLogDelete(args: HazWasteBackendTypes['hazWasteGenerationLogDelete']['request']) {
      return hazWasteBackendRequest('hazWasteGenerationLogDelete', args);
    },
    hazWasteGenerationLogPopulateFromManifests(
      args: HazWasteBackendTypes['hazWasteGenerationLogPopulateFromManifests']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteGenerationLogPopulateFromManifests', args);
    },
    hazWasteGeneratorStatusGet(args: HazWasteBackendTypes['hazWasteGeneratorStatusGet']['request']) {
      return hazWasteBackendRequest('hazWasteGeneratorStatusGet', args);
    },
    hazWasteManifestsList(args: HazWasteBackendTypes['hazWasteManifestsList']['request']) {
      return hazWasteBackendRequest('hazWasteManifestsList', args);
    },
    hazWasteManifestSummariesList(args: HazWasteBackendTypes['hazWasteManifestSummariesList']['request']) {
      return hazWasteBackendRequest('hazWasteManifestSummariesList', args);
    },
    hazWasteManifestGet(args: HazWasteBackendTypes['hazWasteManifestGet']['request']) {
      return hazWasteBackendRequest('hazWasteManifestGet', args);
    },
    hazWasteManifestPartyOptionsList(args: HazWasteBackendTypes['hazWasteManifestPartyOptionsList']['request']) {
      return hazWasteBackendRequest('hazWasteManifestPartyOptionsList', args);
    },
    hazWasteExceptionReportTasksList(args: HazWasteBackendTypes['hazWasteExceptionReportTasksList']['request']) {
      return hazWasteBackendRequest('hazWasteExceptionReportTasksList', args);
    },
    standardShipmentsList(args: HazWasteBackendTypes['standardShipmentsList']['request']) {
      return hazWasteBackendRequest('standardShipmentsList', args);
    },
    standardShipmentCreate(args: HazWasteBackendTypes['standardShipmentCreate']['request']) {
      return hazWasteBackendRequest('standardShipmentCreate', args);
    },
    hazWasteEManifestSiteLookup(args: HazWasteBackendTypes['hazWasteEManifestSiteLookup']['request']) {
      return hazWasteBackendRequest('hazWasteEManifestSiteLookup', args);
    },
    hazWasteStreamShipmentsList(args: HazWasteBackendTypes['hazWasteStreamShipmentsList']['request']) {
      return hazWasteBackendRequest('hazWasteStreamShipmentsList', args);
    },
    hazWasteEManifestStreamImportCandidates(
      args: HazWasteBackendTypes['hazWasteEManifestStreamImportCandidates']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteEManifestStreamImportCandidates', args);
    },
    hazWasteManifestsSync(args: HazWasteBackendTypes['hazWasteManifestsSync']['request']) {
      return hazWasteBackendRequest('hazWasteManifestsSync', args);
    },
    hazWasteManifestLineAssignStream(args: HazWasteBackendTypes['hazWasteManifestLineAssignStream']['request']) {
      return hazWasteBackendRequest('hazWasteManifestLineAssignStream', args);
    },
    hazWasteManifestCreate(args: HazWasteBackendTypes['hazWasteManifestCreate']['request']) {
      return hazWasteBackendRequest('hazWasteManifestCreate', args);
    },
    hazWasteManifestSignAsGenerator(args: HazWasteBackendTypes['hazWasteManifestSignAsGenerator']['request']) {
      return hazWasteBackendRequest('hazWasteManifestSignAsGenerator', args);
    },
    hazWasteManifestExceptionReportSave(args: HazWasteBackendTypes['hazWasteManifestExceptionReportSave']['request']) {
      return hazWasteBackendRequest('hazWasteManifestExceptionReportSave', args);
    },
    hazWasteOnsiteManagementList(args: HazWasteBackendTypes['hazWasteOnsiteManagementList']['request']) {
      return hazWasteBackendRequest('hazWasteOnsiteManagementList', args);
    },
    hazWasteOnsiteManagementSave(args: HazWasteBackendTypes['hazWasteOnsiteManagementSave']['request']) {
      return hazWasteBackendRequest('hazWasteOnsiteManagementSave', args);
    },
    hazWasteOnsiteManagementDelete(args: HazWasteBackendTypes['hazWasteOnsiteManagementDelete']['request']) {
      return hazWasteBackendRequest('hazWasteOnsiteManagementDelete', args);
    },
    hazWasteManifestLineSetManagementMethod(
      args: HazWasteBackendTypes['hazWasteManifestLineSetManagementMethod']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteManifestLineSetManagementMethod', args);
    },
    hazWasteBiennialReportPreview(args: HazWasteBackendTypes['hazWasteBiennialReportPreview']['request']) {
      return hazWasteBackendRequest('hazWasteBiennialReportPreview', args);
    },
    hazWasteBiennialReportGenerate(args: HazWasteBackendTypes['hazWasteBiennialReportGenerate']['request']) {
      return hazWasteBackendRequest('hazWasteBiennialReportGenerate', args);
    },
    hazWasteBiennialReportDownload(args: HazWasteBackendTypes['hazWasteBiennialReportDownload']['request']) {
      return hazWasteBackendRequest('hazWasteBiennialReportDownload', args);
    },
    hazWasteBiennialReportsList(args: HazWasteBackendTypes['hazWasteBiennialReportsList']['request']) {
      return hazWasteBackendRequest('hazWasteBiennialReportsList', args);
    },
    epaHandlerLookup(args: HazWasteBackendTypes['epaHandlerLookup']['request']) {
      return hazWasteBackendRequest('epaHandlerLookup', args);
    },
    hazWasteQuickReferenceGuideGet(args: HazWasteBackendTypes['hazWasteQuickReferenceGuideGet']['request']) {
      return hazWasteBackendRequest('hazWasteQuickReferenceGuideGet', args);
    },
    hazWasteQuickReferenceGuideSave(args: HazWasteBackendTypes['hazWasteQuickReferenceGuideSave']['request']) {
      return hazWasteBackendRequest('hazWasteQuickReferenceGuideSave', args);
    },
    hazWasteQuickReferenceGuideExportPdf(
      args: HazWasteBackendTypes['hazWasteQuickReferenceGuideExportPdf']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteQuickReferenceGuideExportPdf', args);
    },
    hazWasteQuickReferenceGuideAiSuggest(
      args: HazWasteBackendTypes['hazWasteQuickReferenceGuideAiSuggest']['request'],
    ) {
      return hazWasteBackendRequest('hazWasteQuickReferenceGuideAiSuggest', args);
    },
  },
};
